import axios from 'axios';
import {
  fetchUnguarded,
  type IDataStatus,
  type IHelperResponse,
  refreshFetch,
  statusCodeHelperResponse,
} from './axios';
import { KEY_STORAGE_TARGET_URL } from '../constants';
import { getCookie, getStorageToken, logger, normalizeErrorForLogs } from 'utils';
import type { RegistrationStepType } from 'interfaces';
import type { AxiosResponse } from 'axios';
import { sendErrorLogs } from './services';

export const getRegistrationStep = async (signal?: AbortSignal) => {
  const axios = refreshFetch();

  const response: AxiosResponse<RegistrationStepType> = await axios({
    method: 'get',
    url: '/fo/v1/registration',
    signal,
  });
  logger('getRegistrationStep response', response);
  return response;
};

export const postRegistrationStepData = async (data: any) => {
  const axios = refreshFetch();

  const response: AxiosResponse<RegistrationStepType | IHelperResponse> = await axios({
    method: 'post',
    url: '/fo/v1/registration',
    data,
  });

  logger('postRegistrationStepData response', response);

  if ((response.data as IDataStatus).status_code === 'fail') {
    /* eslint-disable */
    const { status_code, data } = response.data as IDataStatus;
    const errorObj = { status_code, message: data.message };
    return await Promise.reject(errorObj);
  }

  if (response.data as RegistrationStepType) {
    return await Promise.resolve(response.data);
  }
};

export interface SaveAdditionalContactPayload {
  additional_phone_number: string;
  additional_phone_name: string;
}

export const saveAdditionalContact = async (payload: SaveAdditionalContactPayload) => {
  const axios = refreshFetch();
  const endpoint = '/fo/v1/contract/addition';

  const response = await axios({
    method: 'post',
    url: endpoint,
    data: {
      phone: payload.additional_phone_number.replace(/\s/g, ''),
      name: payload.additional_phone_name.trim(),
    },
  });

  logger('saveAdditionalContact response', response);
  return await statusCodeHelperResponse(response);
};

export const postDNIPhotoBlob = async (
  endpoint: string,
  image: any,
  asStream: boolean,
  enableExplicitHeaderAndRetries: boolean,
) => {
  const url = '/fo/v1/registration/' + endpoint;
  const axios = refreshFetch();

  if (!image || !(image instanceof Blob)) {
    throw new Error('Image must be a Blob');
  }

  const formData = new FormData();
  formData.append('imageAsBlob', image);
  formData.append('asStream', asStream.toString());

  const isNetworkError = (error: any, hasExplicitHeader: boolean): boolean => {
    const message = typeof error?.message === 'string' ? error.message.toLowerCase() : '';
    const result = error?.code === 'ERR_NETWORK' || message.includes('network error');

    if (result) {
      sendErrorLogs({
        stage: 'post dni photo: network_error',
        endpoint,
        has_explicit_header: hasExplicitHeader,
        error: normalizeErrorForLogs(error),
      });
    }

    return result;
  };

  const sendRequest = async (options: { hasExplicitHeader: boolean }) => {
    const { hasExplicitHeader } = options;
    const response: AxiosResponse<RegistrationStepType | IHelperResponse> = await axios({
      method: 'post',
      url,
      data: formData,
      ...(hasExplicitHeader ? { headers: { 'Content-Type': 'multipart/form-data' } } : {}),
    } as any);

    return response;
  };

  const requestWithRetries = async (): Promise<AxiosResponse<RegistrationStepType | IHelperResponse>> => {
    try {
      return await sendRequest({ hasExplicitHeader: false });
    } catch (error: any) {
      if (!enableExplicitHeaderAndRetries) {
        throw error;
      }

      if (!isNetworkError(error, false)) {
        throw error;
      }

      let lastError: any = error;
      for (let retryAttempt = 0; retryAttempt < 2; retryAttempt++) {
        try {
          const retryResponse = await sendRequest({ hasExplicitHeader: true });

          sendErrorLogs({
            stage: 'post dni photo: successfull retry with headers',
            error: normalizeErrorForLogs(
              new Error(`Successfull retry attempt ${retryAttempt + 1} with headers multiform`),
            ),
          });

          return retryResponse;
        } catch (retryError: any) {
          lastError = retryError;
          if (!isNetworkError(retryError, true)) {
            throw retryError;
          }
        }
      }

      throw lastError;
    }
  };

  const response = await requestWithRetries();

  logger('postDNIPhotoBlob response', response);

  if ((response.data as IDataStatus).status_code === 'fail') {
    /* eslint-disable */
    const { status_code, data } = response.data as IDataStatus;
    const errorObj = { status_code, message: data.message };
    return await Promise.reject(errorObj);
  }

  if (response.data as RegistrationStepType) {
    return await Promise.resolve(response.data);
  }
};

export const getUserInfo = async (signal?: AbortSignal) => {
  // axiosApiInstance allows missing-token requests; personal-info must not hit API without auth.
  if (!getStorageToken()) {
    throw new axios.CanceledError('Missing auth token');
  }

  const client = refreshFetch();
  const endpoint = '/fo/v1/personal-account/personal-info';

  const response = await client({
    method: 'get',
    url: endpoint,
    signal,
  });
  logger('getUserInfo response', response);
  return response;
};

export const getSlug = async (payload: any) => {
  const axios = refreshFetch();
  const endpoint = '/fo/short-link';

  const response = await axios({
    method: 'post',
    url: endpoint,
    data: payload,
  });
  logger('getSlug response', response);

  return await statusCodeHelperResponse(response);
};

export const getUserNotifications = async () => {
  const axios = refreshFetch();
  const endpoint = '/fo/v1/notification';

  const response = await axios({
    method: 'get',
    url: endpoint,
  });
  logger('getUserNotifications response', response);

  return await statusCodeHelperResponse(response);
};

export const getCodeForBrowserRedirect = async () => {
  const axios = refreshFetch();
  const endpoint = '/fo/v1/session/otp';

  const response = await axios({
    method: 'post',
    url: endpoint,
  });
  logger('getCodeForBrowserRedirect response', response);
  return response;
};

export const confirmSessionCode = async (data: { code: string; user_additional_data?: any }) => {
  const axios = fetchUnguarded();
  const endpoint = '/auth/v1/confirm-session-code';
  const cookieTargetUrl = getCookie(KEY_STORAGE_TARGET_URL) || null;
  const mergedUserAdditional = {
    ...(data.user_additional_data ?? {}),
    ...(cookieTargetUrl ? { target_url: cookieTargetUrl } : {}),
  };
  const payload = {
    ...data,
    user_additional_data: mergedUserAdditional,
  };

  const response = await axios({
    method: 'post',
    url: endpoint,
    data: payload,
  });

  logger('confirmSessionCode response', response);
  return await statusCodeHelperResponse(response);
};

export const checkFeatureFlags = async () => {
  const axios = refreshFetch();
  const endpoint = 'fo/v1/feature-flags';
  const response = await axios({
    method: 'get',
    url: endpoint,
  });
  logger('checkFeatureFlags response', response);
  return await statusCodeHelperResponse(response);
};
